import React, { useState, useEffect, useMemo } from "react";
import { X, AlertTriangle } from "lucide-react";
import type { Campaign } from "../../types/campaign";
import { useCityStore } from "../../store/useCityStore";
import { BikeMultiSelect } from "../common/BikeMultiSelect";

interface CampaignModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "delete";
  campaign?: Campaign | null;
  onSave: (campaignData: Record<string, unknown>) => Promise<void>;
}

export const CampaignModal: React.FC<CampaignModalProps> = ({
  open,
  onClose,
  mode,
  campaign,
  onSave,
}) => {
  const { cities } = useCityStore();

  const [formData, setFormData] = useState({
    campaign_id: "",
    start_date: "",
    end_date: "",
    bike_ids: [] as string[],
    city_ids: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Reset form when modal opens/closes or campaign changes
  useEffect(() => {
    if (open) {
      if (mode === "create") {
        setFormData({
          campaign_id: "",
          start_date: "",
          end_date: "",
          bike_ids: [],
          city_ids: [],
        });
      } else if (campaign && (mode === "edit" || mode === "delete")) {
        setFormData({
          campaign_id: campaign.campaign_id,
          start_date: campaign.start_date,
          end_date: campaign.end_date,
          bike_ids: campaign.bike_ids
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id.length > 0),
          city_ids: campaign.city_ids
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id.length > 0),
        });
      }
      setError("");
    }
  }, [open, mode, campaign]);

  const citiesForDisplay = useMemo(() => {
    const selected: typeof cities = [];
    const unselected: typeof cities = [];

    cities.forEach((city) => {
      if (formData.city_ids.includes(city.city_id)) {
        selected.push(city);
      } else {
        unselected.push(city);
      }
    });

    return [...selected, ...unselected];
  }, [cities, formData.city_ids]);

  const handleCityToggle = (cityId: string) => {
    setFormData((prev) => ({
      ...prev,
      city_ids: prev.city_ids.includes(cityId)
        ? prev.city_ids.filter((id) => id !== cityId)
        : [...prev.city_ids, cityId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (mode === "create" || mode === "edit") {
      // Validate form
      if (!formData.campaign_id.trim()) {
        setError("Campaign ID is required");
        return;
      }
      if (!formData.start_date) {
        setError("Start date is required");
        return;
      }
      if (!formData.end_date) {
        setError("End date is required");
        return;
      }
      if (formData.end_date < formData.start_date) {
        setError("End date must be on or after start date");
        return;
      }
      if (formData.bike_ids.length === 0) {
        setError("At least one bike must be selected");
        return;
      }
      if (formData.city_ids.length === 0) {
        setError("At least one city must be selected");
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === "delete") {
        await onSave({});
      } else if (mode === "edit") {
        await onSave({
          start_date: formData.start_date,
          end_date: formData.end_date,
          bike_ids: formData.bike_ids.join(","),
          city_ids: formData.city_ids.join(","),
        });
      } else {
        await onSave({
          campaign_id: formData.campaign_id.trim(),
          start_date: formData.start_date,
          end_date: formData.end_date,
          bike_ids: formData.bike_ids.join(","),
          city_ids: formData.city_ids.join(","),
        });
      }
      onClose();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return "Add New Campaign";
      case "edit":
        return "Edit Campaign";
      case "delete":
        return "Delete Campaign";
      default:
        return "";
    }
  };

  const getSubmitButtonText = () => {
    if (loading) return "Processing...";
    switch (mode) {
      case "create":
        return "Add Campaign";
      case "edit":
        return "Save Changes";
      case "delete":
        return "Delete Campaign";
      default:
        return "Save";
    }
  };

  const getSubmitButtonClass = () => {
    const baseClass =
      "inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    if (mode === "delete") {
      return `${baseClass} text-white bg-red-600 hover:bg-red-700 focus:ring-red-500`;
    }
    return `${baseClass} text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500`;
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Center modal on screen */}
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {getModalTitle()}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                  <span className="text-sm text-red-600">{error}</span>
                </div>
              </div>
            )}

            {/* Content */}
            {mode === "delete" ? (
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the campaign{" "}
                      <span className="font-semibold">
                        {campaign?.campaign_id}
                      </span>
                      ? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Campaign ID */}
                <div>
                  <label
                    htmlFor="campaign_id"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Campaign ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="campaign_id"
                    value={formData.campaign_id}
                    onChange={(e) =>
                      setFormData({ ...formData, campaign_id: e.target.value })
                    }
                    disabled={mode === "edit"}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., summer_2025_campaign"
                  />
                  {mode === "edit" && (
                    <p className="mt-1 text-xs text-gray-500">
                      Campaign ID cannot be changed after creation
                    </p>
                  )}
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="start_date"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      value={formData.start_date}
                      onChange={(e) =>
                        setFormData({ ...formData, start_date: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="end_date"
                      className="block text-sm font-medium text-gray-700"
                    >
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      value={formData.end_date}
                      onChange={(e) =>
                        setFormData({ ...formData, end_date: e.target.value })
                      }
                      min={formData.start_date}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <BikeMultiSelect
                  label="Select Bikes"
                  required
                  selectedBikeIds={formData.bike_ids}
                  onChange={(bike_ids) =>
                    setFormData((prev) => ({ ...prev, bike_ids }))
                  }
                />

                {/* Cities Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Cities <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({formData.city_ids.length} selected)
                    </span>
                  </label>
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                    {cities.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No cities available
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {citiesForDisplay.map((city) => (
                          <label
                            key={city.city_id}
                            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={formData.city_ids.includes(city.city_id)}
                              onChange={() => handleCityToggle(city.city_id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-900">
                              {city.city_name} ({city.country_code})
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={getSubmitButtonClass()}
            >
              {getSubmitButtonText()}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
